/**
 * @letsdoit/due-parser — deterministic, rule-based natural-language due dates compatible with the
 * Todoist API v1 due object (guides/Due_dates.md). English and Chinese, no runtime dependencies;
 * timezones go through `Intl`, so it runs in Workers, Node, browsers and React Native (Hermes Intl).
 */
import {
  dayNumber,
  formatDate,
  formatDateTime,
  fromWallClock,
  isoUtc,
  isValidTimezone,
  type Time,
  wallClock,
} from "./calendar";
import { toTime } from "./common";
import { parseEn } from "./en";
import { firstOnOrAfter, nextAfter } from "./rule";
import type { Due, DueStringInput, Edit, Env, Lang, ParseContext, Parsed, ParseError, ParseResult } from "./types";
import { parseZh } from "./zh";

export { fromLocal, isValidTimezone, timezoneSource, toLocal } from "./calendar";
export type {
  Due,
  DueStringInput,
  Lang,
  ParseContext,
  ParseError,
  ParseErrorCode,
  ParseResult,
  Rule,
} from "./types";

export interface NextOccurrenceContext {
  /** When the task was completed (the reference for `every!` and for skipping missed dates). */
  completedAt: Date;
  /** The user's timezone, used for floating and full-day dues. */
  userTimezone: string;
  startDay?: number;
  nextWeek?: number;
  weekendStartDay?: number;
}

const CJK = /[㐀-鿿]/;

/** Guesses the language of a due string: any CJK ideograph means `zh`, otherwise `en`. */
export function detectLang(s: string): Lang {
  return CJK.test(s) ? "zh" : "en";
}

/** Full-width ASCII → ASCII (！ → !, ３ → 3, ： → :), ideographic space → space. Length-preserving. */
function widthFold(s: string): string {
  return s.replace(/[！-～]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/　/g, " ");
}

function normalize(s: string): string {
  return widthFold(s).replace(/\s+/g, " ").trim();
}

/** ASCII-only lowercase keeps string indices stable for edits. */
const lower = (s: string) => s.replace(/[A-Z]/g, (c) => c.toLowerCase());

const TZ_SUFFIX = /\s+((?:[A-Za-z_]+\/[A-Za-z0-9_+-]+(?:\/[A-Za-z0-9_+-]+)?)|UTC|GMT)$/;

const PARSERS: Record<Lang, (s: string, env: Env) => Parsed | null> = { en: parseEn, zh: parseZh };

function runParsers(s: string, lang: Lang, env: Env): { parsed: Parsed; lang: Lang } | null {
  for (const l of [lang, lang === "en" ? "zh" : "en"] as Lang[]) {
    const parsed = PARSERS[l](lower(s), env);
    if (parsed) return { parsed, lang: l };
  }
  return null;
}

function makeEnv(now: Date, tz: string, ctx: { startDay?: number; nextWeek?: number; weekendStartDay?: number }): Env {
  const w = wallClock(now, tz);
  return {
    today: w.dn,
    nowMinutes: w.h * 60 + w.m,
    startDay: ctx.startDay ?? 1,
    nextWeek: ctx.nextWeek ?? 1,
    weekendStartDay: ctx.weekendStartDay ?? 6,
  };
}

function applyEdits(s: string, edits: Edit[] | undefined): string {
  let out = s;
  for (const e of [...(edits ?? [])].sort((a, b) => b.from - a.from)) {
    out = out.slice(0, e.from) + e.text + out.slice(e.to);
  }
  return out;
}

const minutesOf = (t: Time) => t.h * 60 + t.m;
const MAX_STEPS = 100_000;

/** Formats a local date (+ optional time) as a due `date`, fixed (UTC + zone) when `zone` is set. */
function formatDue(dn: number, time: Time | undefined, zone: string | null): { date: string; timezone: string | null } {
  if (!time) return { date: formatDate(dn), timezone: null };
  if (zone) return { date: isoUtc(fromWallClock(dn, time, zone)), timezone: zone };
  return { date: formatDateTime(dn, time), timezone: null };
}

const error = (code: ParseError["code"], message: string): ParseError => ({ ok: false, code, message });

/** First occurrence of a recurring parse on or after now. */
function firstOccurrence(p: Extract<Parsed, { kind: "recur" }>, env: Env): { dn: number; time?: Time } {
  const { rule } = p;
  const nowPos = env.today * 1440 + env.nowMinutes;
  if (rule.freq === "hour") {
    const t = p.startTime ?? p.time;
    const step = rule.interval * 60;
    let pos: number;
    if (t) pos = (p.start?.dn ?? env.today) * 1440 + minutesOf(t);
    else if (p.start) pos = p.start.dn * 1440;
    else pos = Math.ceil((nowPos + 1) / 60) * 60;
    for (let i = 0; pos < nowPos && i < MAX_STEPS; i++) pos += step;
    return { dn: Math.floor(pos / 1440), time: toTime(pos % 1440) };
  }
  const time = p.time ?? p.startTime;
  let dn = firstOnOrAfter(rule, p.start?.dn ?? env.today);
  for (let i = 0; dn < env.today && i < MAX_STEPS; i++) dn = nextAfter(rule, dn, env.startDay);
  // Only future dates: a time that already passed today moves to the next occurrence.
  if (time && dn === env.today && minutesOf(time) <= env.nowMinutes) dn = nextAfter(rule, dn, env.startDay);
  return { dn, time };
}

/**
 * Parses a natural-language due string ("tomorrow at 5pm", "every! 3 days", "每周一三五 早上9点")
 * into the official due object. Relative words are resolved in the user's timezone (or in the
 * explicit `timezone`, which also makes timed dates fixed instead of floating).
 */
export function parseDue(input: DueStringInput, ctx: ParseContext): ParseResult {
  const original = input.string ?? "";
  let norm = normalize(original);
  if (!norm) return error("EMPTY", "Empty due string");
  if (input.timezone != null && !isValidTimezone(input.timezone)) return error("INVALID_TIMEZONE", "Unknown timezone");
  let zone = input.timezone ?? null;
  const tzMatch = TZ_SUFFIX.exec(norm);
  if (tzMatch && isValidTimezone(tzMatch[1] as string)) {
    zone ??= tzMatch[1] as string;
    norm = norm.slice(0, tzMatch.index);
  }
  const requested: Lang = input.lang === "zh" || input.lang === "en" ? input.lang : detectLang(norm);
  const env = makeEnv(ctx.now, zone ?? ctx.userTimezone, ctx);
  const result = runParsers(norm, requested, env);
  if (!result) return error("UNPARSEABLE", `Unable to parse due string: ${original.trim()}`);
  const { parsed, lang } = result;
  if (parsed.kind === "clear") return { ok: true, due: null };

  if (parsed.kind === "once") {
    return {
      ok: true,
      due: { ...formatDue(parsed.dn, parsed.time, zone), string: original.trim(), lang, is_recurring: false },
    };
  }
  const first = firstOccurrence(parsed, env);
  const edited = parsed.edits?.length ? applyEdits(normalize(original), parsed.edits) : original.trim();
  return {
    ok: true,
    due: { ...formatDue(first.dn, first.time, zone), string: edited, lang, is_recurring: true },
  };
}

/** Parses only the recurrence part of a due string; null when it isn't recurring. */
function parseRecurrence(due: Due, env: Env): Extract<Parsed, { kind: "recur" }> | null {
  let norm = normalize(due.string);
  const tzMatch = TZ_SUFFIX.exec(norm);
  if (tzMatch && isValidTimezone(tzMatch[1] as string)) norm = norm.slice(0, tzMatch.index);
  const lang: Lang = due.lang === "zh" || due.lang === "en" ? due.lang : detectLang(norm);
  const r = runParsers(norm, lang, env);
  return r?.parsed.kind === "recur" ? r.parsed : null;
}

const LOCAL_RE = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/;

/**
 * The next occurrence of a recurring due after a completion, or null when the due isn't recurring,
 * its rule can't be understood, or the recurrence has ended (`until` / `ending` / 直到).
 *
 * - `every …` counts from the current due date, then skips missed dates so the result is in the future.
 * - `every! …` / `after …` counts from the completion date (keeping the due's time of day).
 */
export function nextOccurrence(due: Due, ctx: NextOccurrenceContext): Due | null {
  if (!due.is_recurring) return null;
  const tz = due.timezone ?? ctx.userTimezone;
  const fixed = due.date.endsWith("Z");
  let dn: number;
  let minutes: number | undefined;
  if (fixed) {
    const w = wallClock(new Date(due.date), tz);
    dn = w.dn;
    minutes = w.h * 60 + w.m;
  } else {
    const m = LOCAL_RE.exec(due.date);
    if (!m) return null;
    dn = dayNumber(Number(m[1]), Number(m[2]), Number(m[3]));
    if (m[4] !== undefined) minutes = Number(m[4]) * 60 + Number(m[5]);
  }
  // Bounds in the string are resolved relative to the current occurrence.
  const env = makeEnv(new Date(), tz, ctx);
  env.today = dn;
  env.nowMinutes = minutes ?? 0;
  const p = parseRecurrence(due, env);
  if (!p) return null;
  const { rule } = p;
  const comp = wallClock(ctx.completedAt, tz);
  const compPos = comp.dn * 1440 + comp.h * 60 + comp.m;

  let nextDn: number;
  let nextMin = minutes;
  if (rule.freq === "hour") {
    const step = rule.interval * 60;
    let pos: number;
    if (rule.fromCompletion) pos = compPos + step;
    else {
      pos = dn * 1440 + (minutes ?? 0) + step;
      for (let i = 0; pos <= compPos && i < MAX_STEPS; i++) pos += step;
    }
    nextDn = Math.floor(pos / 1440);
    nextMin = pos % 1440;
  } else {
    const startDay = env.startDay;
    const passed = (d: number) => (minutes !== undefined ? d * 1440 + minutes <= compPos : d <= comp.dn);
    nextDn = nextAfter(rule, rule.fromCompletion ? comp.dn : dn, startDay);
    for (let i = 0; passed(nextDn) && i < MAX_STEPS; i++) nextDn = nextAfter(rule, nextDn, startDay);
    // Start bounds only matter for the first occurrence (computed by parseDue); a year-less start
    // re-resolved against a later due would wrongly land in the following year.
  }
  if (p.end && nextDn > p.end.dn) return null;
  const time = nextMin === undefined ? undefined : toTime(nextMin);
  const { date } = formatDue(nextDn, time, fixed ? tz : null);
  return { ...due, date };
}
